import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PersonId } from '@shared/brand';
import type { PermissionScope, RequestScope } from '@application/ports';
import { getServices } from '@infrastructure/services';
import { useSession } from '../providers/SessionProvider';

/**
 * Which absences a session may see in an approval queue.
 *
 * An administrator sees the whole area. A leader sees **the people who name them
 * as their leader** — not their sub área.
 *
 * That distinction was the bug. The board filtered by sub área, but leadership
 * is assigned per person in `personLeaders` and routinely crosses sub áreas: in
 * Tecnología one leader signs for twenty-one people, eighteen of them outside
 * her own. Her queue asked Firestore for `subAreaId == po`, so a request from
 * Backend that named her as the first signature never appeared anywhere she
 * could see it, and nobody could tell it was waiting on her. Sub área is where
 * somebody works; it was never who approves them.
 *
 * The read widens to the area — a query cannot say «these twenty-one people»,
 * and `personId in [...]` combined with a status filter blows past what
 * Firestore will disjoin — and the people are picked out afterwards. The rules
 * already allow any active member to list their own area, so this uses access
 * that existed rather than granting new access; the server still re-checks every
 * decision against the same links, refusing a decider who is not a named leader
 * of the requester.
 *
 * Excluding their own requests is the other half. A leader who found their own
 * request in the queue could act on it, and self-approval is precisely what the
 * double sign-off exists to prevent.
 */
export interface ApprovalScope {
  /** Feed straight into a repository scope. */
  readonly scope: RequestScope;
  /** Applied after the read; a scope cannot express "not this person". */
  readonly excludesOwn: boolean;
  /**
   * The links are not in yet. A queue filtered before they land looks empty, so
   * callers wait rather than showing a leader an emptiness that is not true.
   */
  readonly loading: boolean;
  /** Whether this session may decide on that person's absences. */
  readonly covers: (personId: PersonId) => boolean;
  /**
   * Keeps only the absences this session may decide on.
   *
   * Both halves of the rule in one call, because applying one and forgetting the
   * other is exactly the mistake that hid a request for days.
   */
  readonly mine: <T extends { readonly personId: PersonId }>(
    items: readonly T[],
  ) => readonly T[];
}

export const useApprovalScope = (): ApprovalScope => {
  const session = useSession();
  const areaWide = session.role === 'admin' || session.role === 'super';

  /*
   * Same key the approvals screen already uses, so the two share one fetch.
   */
  const links = useQuery({
    queryKey: ['approvals', 'leaders', session.areaId],
    enabled: !areaWide,
    queryFn: async () => {
      const result = await getServices().leaders.listByArea(session.areaId);
      return result.ok ? result.value : [];
    },
  });

  const led = useMemo(
    () =>
      new Set(
        (links.data ?? [])
          .filter((link) => link.leaderPersonId === session.personId)
          .map((link) => link.personId),
      ),
    [links.data, session.personId],
  );

  return useMemo(() => {
    const scope: RequestScope = { areaId: session.areaId };

    if (areaWide) {
      return {
        scope,
        excludesOwn: false,
        loading: false,
        covers: () => true,
        mine: (items) => items,
      };
    }

    const covers = (personId: PersonId): boolean =>
      personId !== session.personId && led.has(personId);

    return {
      scope,
      excludesOwn: true,
      loading: links.isPending,
      covers,
      mine: (items) => items.filter((item) => covers(item.personId)),
    };
  }, [areaWide, session.areaId, session.personId, led, links.isPending]);
};

/**
 * The location half of an approval scope: the keys every absence collection
 * shares.
 *
 * `RequestScope` also carries `statuses` and `touching`, which are typed against
 * the vacation vocabulary — spreading the whole thing into a permit or
 * certificate query would either fail to compile or, worse, quietly carry a
 * status set that means nothing there. Narrowing here says which part actually
 * transfers.
 *
 * Written as conditional spreads rather than assignments because
 * `exactOptionalPropertyTypes` distinguishes an absent key from one set to
 * `undefined`, and a scope with `areaId: undefined` is not the same query.
 */
export const placeOf = (
  scope: RequestScope,
): Pick<PermissionScope, 'areaId' | 'subAreaId' | 'personId'> => ({
  ...(scope.areaId === undefined ? {} : { areaId: scope.areaId }),
  ...(scope.subAreaId === undefined ? {} : { subAreaId: scope.subAreaId }),
  ...(scope.personId === undefined ? {} : { personId: scope.personId }),
});
